import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2,
  CheckCircle,
  AlertTriangle,
  LogIn,
  Loader2,
  Clock,
  ArrowLeft,
  UserPlus,
} from "lucide-react";
import { invitationsApi, type InvitationInfo } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Spinner } from "@/components/ui/spinner";
import { AuthModal, type AuthModalView } from "@/components/auth/AuthModal";
import logoSvg from "@/assets/logo.svg";

const ROLE_LABELS: Record<string, string> = {
  owner: "Ägare",
  manager: "Administratör",
  editor: "Redigerare",
  viewer: "Läsbehörighet",
};

const ROLE_BADGE_STYLES: Record<string, string> = {
  owner: "bg-amber-100 text-amber-800 border-amber-200",
  manager: "bg-blue-100 text-blue-800 border-blue-200",
  editor: "bg-green-100 text-green-800 border-green-200",
  viewer: "bg-gray-100 text-gray-700 border-gray-200",
};

type PageStatus =
  | "loading"
  | "ready"
  | "not_authenticated"
  | "expired"
  | "already_accepted"
  | "error"
  | "accepting"
  | "accepted";

export function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const token = searchParams.get("token");

  const [status, setStatus] = useState<PageStatus>("loading");
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authView, setAuthView] = useState<AuthModalView>("register");

  // Fetch invitation info when token is available and auth state is resolved
  useEffect(() => {
    if (authLoading) return;
    if (!token) {
      setStatus("error");
      setErrorMessage("Ingen inbjudningstoken hittades i länken.");
      return;
    }

    const fetchInvitationInfo = async () => {
      try {
        const response = await invitationsApi.getInfo(token);

        if (response.success && response.data) {
          const inv = response.data.invitation;
          setInvitation(inv);

          // Check if already accepted
          if (inv.acceptedAt) {
            setStatus("already_accepted");
            return;
          }

          // Check if expired
          const expiresAt = new Date(inv.expiresAt);
          if (expiresAt < new Date()) {
            setStatus("expired");
            return;
          }

          // Check auth status
          if (!isAuthenticated) {
            setStatus("not_authenticated");
            return;
          }

          setStatus("ready");
        } else {
          setStatus("error");
          setErrorMessage(
            response.error?.message ||
              "Kunde inte hämta information om inbjudan."
          );
        }
      } catch {
        setStatus("error");
        setErrorMessage("Ett oväntat fel uppstod. Försök igen senare.");
      }
    };

    fetchInvitationInfo();
  }, [token, isAuthenticated, authLoading]);

  const handleAccept = async () => {
    if (!token) return;

    setStatus("accepting");

    try {
      const response = await invitationsApi.accept(token);

      if (response.success && response.data) {
        setStatus("accepted");
        const companyName =
          response.data.companyName || invitation?.companyName || "företaget";
        toast.success(`Du är nu medlem i ${companyName}!`);

        // Navigate to the company after a brief moment
        setTimeout(() => {
          navigate(
            `/dashboard/companies/${response.data!.companyId}`,
            { replace: true }
          );
        }, 1500);
      } else {
        setStatus("ready");
        const msg =
          response.error?.message || "Kunde inte acceptera inbjudan.";
        toast.error(msg);
        setErrorMessage(msg);
      }
    } catch {
      setStatus("ready");
      toast.error("Ett fel uppstod. Försök igen.");
    }
  };

  const formatExpiryDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Show spinner while auth is loading
  if (authLoading || status === "loading") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/">
              <img
                src={logoSvg}
                alt="Kvanten"
                className="h-10 w-auto mx-auto"
              />
            </Link>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="flex justify-center mb-6">
              <Spinner size="lg" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Hämtar inbjudan...
            </h2>
            <p className="text-gray-600">
              Vänta medan vi hämtar information om din inbjudan.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src={logoSvg}
              alt="Kvanten"
              className="h-10 w-auto mx-auto"
            />
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-lg w-full p-8 text-center">
          {/* ── Error: Invalid token or fetch failed ── */}
          {status === "error" && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="text-red-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Ogiltig inbjudan
              </h2>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              <Link
                to={isAuthenticated ? "/dashboard" : "/"}
                className="btn btn-primary w-full inline-flex items-center justify-center space-x-2"
              >
                <ArrowLeft size={20} />
                <span>
                  {isAuthenticated
                    ? "Gå till Dashboard"
                    : "Gå till startsidan"}
                </span>
              </Link>
            </>
          )}

          {/* ── Expired invitation ── */}
          {status === "expired" && invitation && (
            <>
              <div className="w-16 h-16 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Clock className="text-amber-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Inbjudan har gått ut
              </h2>
              <p className="text-gray-600 mb-2">
                Inbjudan till{" "}
                <span className="font-semibold text-gray-900">
                  {invitation.companyName}
                </span>{" "}
                gick ut den{" "}
                <span className="font-medium">
                  {formatExpiryDate(invitation.expiresAt)}
                </span>
                .
              </p>
              <p className="text-gray-500 text-sm mb-6">
                Be företagets ägare att skicka en ny inbjudan.
              </p>
              <Link
                to={isAuthenticated ? "/dashboard" : "/"}
                className="btn btn-primary w-full inline-flex items-center justify-center space-x-2"
              >
                <ArrowLeft size={20} />
                <span>
                  {isAuthenticated
                    ? "Gå till Dashboard"
                    : "Gå till startsidan"}
                </span>
              </Link>
            </>
          )}

          {/* ── Already accepted ── */}
          {status === "already_accepted" && invitation && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-green-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Inbjudan redan accepterad
              </h2>
              <p className="text-gray-600 mb-6">
                Du är redan medlem i{" "}
                <span className="font-semibold text-gray-900">
                  {invitation.companyName}
                </span>
                .
              </p>
              {isAuthenticated ? (
                <Link
                  to={`/dashboard/companies/${invitation.companyId}`}
                  className="btn btn-primary w-full inline-flex items-center justify-center space-x-2"
                >
                  <Building2 size={20} />
                  <span>Gå till {invitation.companyName}</span>
                </Link>
              ) : (
                <Link
                  to="/"
                  state={{ redirectTo: `/dashboard/companies/${invitation.companyId}` }}
                  className="btn btn-primary w-full inline-flex items-center justify-center space-x-2"
                >
                  <LogIn size={20} />
                  <span>Logga in för att se företaget</span>
                </Link>
              )}
            </>
          )}

          {/* ── Not authenticated ── */}
          {status === "not_authenticated" && invitation && (
            <>
              <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <UserPlus className="text-primary-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Acceptera inbjudan
              </h2>
              <p className="text-gray-600 mb-2">
                Du har blivit inbjuden till{" "}
                <span className="font-semibold text-gray-900">
                  {invitation.companyName}
                </span>
                .
              </p>
              <p className="text-gray-600 mb-6">
                Inbjudan skickades till{" "}
                <span className="font-medium text-gray-900">
                  {invitation.email}
                </span>
              </p>

              {/* Invitation preview */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="text-primary-600" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {invitation.companyName}
                    </p>
                    <p className="text-sm text-gray-500">
                      Roll:{" "}
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                          ROLE_BADGE_STYLES[invitation.role] ||
                          ROLE_BADGE_STYLES.viewer
                        }`}
                      >
                        {ROLE_LABELS[invitation.role] || invitation.role}
                      </span>
                    </p>
                  </div>
                </div>
                <p className="text-xs text-gray-400">
                  Går ut: {formatExpiryDate(invitation.expiresAt)}
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    // Store invitation URL so we can return after email verification
                    sessionStorage.setItem("pendingInvitationUrl", window.location.href);
                    setAuthView("register");
                    setShowAuthModal(true);
                  }}
                  className="btn btn-primary w-full inline-flex items-center justify-center space-x-2"
                >
                  <UserPlus size={20} />
                  <span>Skapa konto</span>
                </button>

                <button
                  onClick={() => {
                    // Store invitation URL so we can return after login
                    sessionStorage.setItem("pendingInvitationUrl", window.location.href);
                    setAuthView("login");
                    setShowAuthModal(true);
                  }}
                  className="btn btn-secondary w-full inline-flex items-center justify-center space-x-2"
                >
                  <LogIn size={20} />
                  <span>Har redan konto? Logga in</span>
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                Har du inget konto skapas ett automatiskt med e-postadressen från inbjudan.
              </p>
            </>
          )}

          {/* ── Ready to accept ── */}
          {(status === "ready" || status === "accepting") && invitation && (
            <>
              <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Building2 className="text-primary-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Du har blivit inbjuden
              </h2>
              <p className="text-gray-600 mb-6">
                Du har fått en inbjudan att gå med i följande företag.
              </p>

              {/* Invitation details */}
              <div className="bg-gray-50 rounded-xl p-5 mb-6 text-left space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="text-primary-600" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-lg">
                      {invitation.companyName}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Din roll</span>
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                        ROLE_BADGE_STYLES[invitation.role] ||
                        ROLE_BADGE_STYLES.viewer
                      }`}
                    >
                      {ROLE_LABELS[invitation.role] || invitation.role}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      Inbjudan går ut
                    </span>
                    <span className="text-sm text-gray-700">
                      {formatExpiryDate(invitation.expiresAt)}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleAccept}
                disabled={status === "accepting"}
                className="btn btn-primary w-full inline-flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {status === "accepting" ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    <span>Accepterar...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle size={20} />
                    <span>Acceptera inbjudan</span>
                  </>
                )}
              </button>
            </>
          )}

          {/* ── Successfully accepted ── */}
          {status === "accepted" && invitation && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-green-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Välkommen!
              </h2>
              <p className="text-gray-600 mb-6">
                Du är nu medlem i{" "}
                <span className="font-semibold text-gray-900">
                  {invitation.companyName}
                </span>
                . Du skickas vidare...
              </p>
              <div className="flex justify-center">
                <Spinner size="default" />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        currentView={authView}
        onViewChange={setAuthView}
        initialEmail={invitation?.email}
        emailLocked={authView === "register"}
        navigateAfterLogin={false}
        onAuthSuccess={() => {
          // The useEffect will automatically update the status when auth state changes
          setShowAuthModal(false);
        }}
      />
    </div>
  );
}
